import React from 'react';
import { FileText, Shield, Lock, Mail, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEO } from './SEO';

const TermsPage: React.FC = () => {
  const { t } = useTranslation(['terms', 'seo']);
  const isGlobal = typeof window !== 'undefined' && (window as any).__isIndianLocale__ === false;

  return (
    <div className="min-h-screen bg-white text-gray-800 py-12 px-4">
      <SEO
        title={t('seo:terms.title', { defaultValue: 'Terms of Service | Sarvalok Legal Terms' })}
        description={t('seo:terms.description', { defaultValue: "Read Sarvalok's terms of service, user agreements, and legal conditions for using our AI platform for cultural research and spiritual studies." })}
        url="/terms-condition"
      />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="text-4xl md:text-5xl font-extrabold text-center mb-8 text-black">
          {t('terms.page_title')}
        </h1>

        {/* Intro Section */}
        <section className="mb-12">
          <p className="text-lg leading-relaxed">
            {t(isGlobal ? 'terms.intro.company_info_global' : 'terms.intro.company_info')}<br/><br/>
            {t('terms.intro.effective_date')}<br/><br/>
            {t('terms.intro.welcome')}<br/><br/>
            {t(isGlobal ? 'terms.intro.terms_description_global' : 'terms.intro.terms_description')}<br/><br/>
            {t('terms.intro.agreement_notice')}<br/><br/>
            {t('terms.intro.electronic_record')}
          </p>
        </section>

        {/* Sections */}
        <section className="space-y-12">
          {/* 1. Who We Are */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <FileText size={28} /> {t('terms.sections.who_we_are.title')}
            </h2>
            <p>
              {t(isGlobal ? 'terms.sections.who_we_are.content_global' : 'terms.sections.who_we_are.content')}
            </p>
          </div>

          {/* 2. Registration and Access */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <Lock size={28} /> {t('terms.sections.registration.title')}
            </h2>
            <p>
              {t('terms.sections.registration.content')}
            </p>
          </div>

          {/* 3. Using Our Services */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('terms.sections.using_services.title')}
            </h2>
            <p>
              {t('terms.sections.using_services.content')}
            </p>
          </div>

          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('terms.sections.fair_use.title', { defaultValue: 'Fair‑Use & Abuse Protection Limits' })}
            </h2>
            <p>
              {t('terms.sections.fair_use.content', {
                defaultValue:
                  'To protect the Service, our infrastructure, and other users, we may apply fair‑use and abuse‑prevention limits (including request‑rate, concurrency, or other technical controls). These controls may vary over time and may be applied per user, per account, per organization, or per network. Attempting to bypass these protections is prohibited and may result in throttling, suspension, or termination.',
              })}
            </p>
          </div>

          {/* 4. Intellectual Property Rights */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('terms.sections.intellectual_property.title')}
            </h2>
            <p>
              {t('terms.sections.intellectual_property.content')}
            </p>
            <p className="mt-4">
              {t('terms.sections.intellectual_property.competitive_use_restriction', {
                defaultValue:
                  'Except to the extent prohibited by applicable law, you may not use the Services, or any proprietary content, compilations, reports, prompts, workflows, or other materials made available by or obtained through the Services, to build, train, improve, benchmark, market, or operate a competing or substantially similar product or service without our prior written consent. If we reasonably believe you have violated this restriction, we may suspend or terminate your access and pursue any rights or remedies available to us, including injunctive or other equitable relief where appropriate.',
              })}
            </p>
          </div>

          {/* 5. Paid Accounts */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('terms.sections.paid_accounts.title')}
            </h2>
            <p>
              {t('terms.sections.paid_accounts.content')}
            </p>
          </div>

          {/* 6. Termination and Suspension */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('terms.sections.termination.title')}
            </h2>
            <p>
              {t('terms.sections.termination.content')}
            </p>
          </div>

          {/* 7. Disclaimer of Warranties */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <Shield size={28} /> {t('terms.sections.disclaimer_warranties.title')}
            </h2>
            <p>
              {t('terms.sections.disclaimer_warranties.content')}
            </p>
          </div>

          {/* 8. Limitation of Liability */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('terms.sections.limitation_liability.title')}
            </h2>
            <p>
              {t('terms.sections.limitation_liability.content')}
            </p>
          </div>

          {/* 9. Indemnity */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('terms.sections.indemnity.title')}
            </h2>
            <p>
              {t('terms.sections.indemnity.content')}
            </p>
          </div>

          {/* 10. Dispute Resolution */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('terms.sections.dispute_resolution.title')}
            </h2>
            <p>
              {t('terms.sections.dispute_resolution.content')}
            </p>
          </div>

          {/* 11. Copyright Complaints */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('terms.sections.copyright_complaints.title')}
            </h2>
            <p>
              {t('terms.sections.copyright_complaints.content')}
            </p>
          </div>

          {/* 12. Contact Information */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black flex items-center gap-2 mb-4">
              <Mail size={28} /> {t('terms.sections.contact_info.title')}
            </h2>
            <p>
              {t(isGlobal ? 'terms.sections.contact_info.content_global' : 'terms.sections.contact_info.content')}
            </p>
          </div>

          {/* 13. Prohibited Searches */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
              {t('terms.sections.prohibited_searches.title')}
            </h2>
            <p>
              {t('terms.sections.prohibited_searches.content')}
            </p>
          </div>
        </section>

        {/* General Terms */}
        <section className="mt-12">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
            {t('terms.general_terms.title')}
          </h2>
          <p>
            {t('terms.general_terms.content')}
          </p>
        </section>

        {/* Dispute Resolution */}
        <section className="mt-12">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
            {t('terms.dispute_resolution_detailed.title')}
          </h2>
          <p>
            {t('terms.dispute_resolution_detailed.content')}
          </p>
        </section>

        {/* Limitation of Liability */}
        <section className="mt-12">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
            {t('terms.limitation_liability_detailed.title')}
          </h2>
          <p>
            {t('terms.limitation_liability_detailed.content')}
          </p>
        </section>

        {/* Disclaimer of Warranties */}
        <section className="mt-12">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
            {t('terms.disclaimer_warranties_detailed.title')}
          </h2>
          <p>
            {t('terms.disclaimer_warranties_detailed.content')}
          </p>
        </section>

        {/* Final Disclaimer */}
        <section className="mt-12">
          <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
            {t('terms.final_disclaimer.title')}
          </h2>
          <p className="mb-4">
            {t('terms.final_disclaimer.intro')}
          </p>
          <ul className="list-disc list-inside space-y-4">
            <h3 className="font-semibold">{t('terms.final_disclaimer.religious_advice.title')}</h3>
            <ul className="list-disc list-inside ml-4">
              <li>{t('terms.final_disclaimer.religious_advice.point1')}</li>
              <li>{t('terms.final_disclaimer.religious_advice.point2')}</li>
              <li>{t('terms.final_disclaimer.religious_advice.point3')}</li>
            </ul>
            
            <h3 className="font-semibold">{t('terms.final_disclaimer.nature_content.title')}</h3>
            <p className="ml-4">{t('terms.final_disclaimer.nature_content.content')}</p>
            
            <h3 className="font-semibold">{t('terms.final_disclaimer.no_endorsement.title')}</h3>
            <p className="ml-4">{t('terms.final_disclaimer.no_endorsement.content')}</p>

            <h3 className="font-semibold">{t('terms.final_disclaimer.personal_interpretation.title')}</h3>
            <p className="ml-4">{t('terms.final_disclaimer.personal_interpretation.content')}</p>
            
            <h3 className="font-semibold">{t('terms.final_disclaimer.professional_guidance.title')}</h3>
            <p className="ml-4">{t('terms.final_disclaimer.professional_guidance.content')}</p>

            <h3 className="font-semibold">
              {t('terms.final_disclaimer.health_ayurveda.title', { defaultValue: 'Health, Ayurveda, and Life-Crisis Content' })}
            </h3>
            <p className="ml-4">
              {t('terms.final_disclaimer.health_ayurveda.content', {
                defaultValue:
                  'Sarvalok may discuss health, Ayurveda, and difficult life situations through dharmic, scriptural, or traditional lenses. Such content is informational in nature and should not be treated as medical, psychiatric, legal, or emergency guidance. For serious or urgent concerns, consult an appropriately qualified professional.',
              })}
            </p>
            
            <h3 className="font-semibold">{t('terms.final_disclaimer.changes.title')}</h3>
            <p className="ml-4">{t('terms.final_disclaimer.changes.content')}</p>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default TermsPage;
